import { useEffect, useRef } from 'react';
import { useCancelableTask } from './useCancelableTask';
import { bootstrapCurveCIFromCounts, bootstrapSolowSmithCI, bootstrapSolowSmithGof } from '../models/bootstrap';
import { computeExpectedCurveCounts } from '../models/curveUtils';
import {
  computeAIC,
  computeAICc,
  computeAkaikeWeights,
  computeHoldoutRMSE,
  computePoissonLogL,
} from '../models/diagnostics';
import { fitGompertz, gompertzCurve } from '../models/gompertz';
import { fitLogistic, logisticCurve } from '../models/logistic';
import { computeExpectedDiscoveryCounts, fitSolowSmithCore, profileSolowSmithCI } from '../models/solowSmith';
import { createBootstrapWorkerClient } from '../utils/bootstrapWorkerClient';

export const useBaseFit = ({
  modelFitPoints,
  modelSolowPoints,
  modelDiscoveryCounts,
  modelStartYear,
  endYear,
  bootstrapSamples,
  curveBandEnabled,
  curveFitMethod,
  randomSeed,
  getRandomFn,
  t,
  runTrigger,
}) => {
  const task = useCancelableTask();
  const workerRef = useRef(null);

  useEffect(() => {
    if (modelFitPoints.length < 5) {
      task.cancel();
      task.reset();
      task.setError(modelFitPoints.length > 0 ? t('estimationPage.insufficientData') : t('noData'));
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      return;
    }

    task.run(async ({ updateStage, updateProgress, setResult, setError, yieldToMain, isActive }) => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      const workerClient = createBootstrapWorkerClient();
      workerRef.current = workerClient;

      try {
        updateStage(t('estimationPage.progressPreparing'), 5);
        await yieldToMain();

        const randomFn = getRandomFn();
        const yieldEvery = 20;

        updateStage(t('estimationPage.progressSolowSmithFit'), 15);
        await yieldToMain();
        const solowSmithBase = fitSolowSmithCore(
          modelDiscoveryCounts,
          modelStartYear,
          endYear,
          modelSolowPoints
        );

        updateStage(t('estimationPage.progressSolowSmithProfile'), 30);
        await yieldToMain();
        const solowSmithProfileCI = solowSmithBase
          ? profileSolowSmithCI(modelDiscoveryCounts, modelStartYear, endYear, solowSmithBase)
          : null;

        let solowSmithBootstrapCI = null;
        if (solowSmithBase && bootstrapSamples > 0) {
          updateStage(t('estimationPage.progressSolowSmithBootstrap'), 45);
          await yieldToMain();
          solowSmithBootstrapCI = workerClient
            ? await workerClient.runJob('BOOTSTRAP_SOLOW_CI', {
                baseFit: solowSmithBase,
                startYear: modelStartYear,
                endYear,
                iterations: bootstrapSamples,
                varyN: true,
                includeCurve: curveBandEnabled,
                randomSeed,
                sharedRandom: Boolean(randomSeed),
              })
            : await bootstrapSolowSmithCI(
                solowSmithBase,
                modelStartYear,
                endYear,
                bootstrapSamples,
                {
                  varyN: true,
                  includeCurve: curveBandEnabled,
                  randomFn,
                  yieldEvery,
                  yieldFn: yieldToMain,
                }
              );
        }

        updateStage(t('estimationPage.progressCurveFit'), 60);
        await yieldToMain();
        const logistic = fitLogistic(modelFitPoints, {
          discoveryCounts: modelDiscoveryCounts,
          modelStartYear,
          fitMethod: curveFitMethod,
        });
        const gompertz = fitGompertz(modelFitPoints, {
          discoveryCounts: modelDiscoveryCounts,
          modelStartYear,
          fitMethod: curveFitMethod,
        });

        let logisticCI = null;
        let gompertzCI = null;
        if (bootstrapSamples > 0) {
          updateStage(t('estimationPage.progressCurveBootstrap'), 80);
          await yieldToMain();
          logisticCI = logistic
            ? workerClient
              ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                  model: 'logistic',
                  discoveryCounts: modelDiscoveryCounts,
                  modelStartYear,
                  iterations: bootstrapSamples,
                  includeCurve: curveBandEnabled,
                  fitMethod: curveFitMethod,
                  randomSeed,
                  sharedRandom: Boolean(randomSeed),
                })
              : await bootstrapCurveCIFromCounts(
                  modelDiscoveryCounts,
                  modelStartYear,
                  fitLogistic,
                  bootstrapSamples,
                  params => params.L,
                  {
                    randomFn,
                    curveFn: logisticCurve,
                    includeCurve: curveBandEnabled,
                    fitMethod: curveFitMethod,
                    yieldEvery,
                    yieldFn: yieldToMain,
                  }
                )
            : null;
          gompertzCI = gompertz
            ? workerClient
              ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                  model: 'gompertz',
                  discoveryCounts: modelDiscoveryCounts,
                  modelStartYear,
                  iterations: bootstrapSamples,
                  includeCurve: curveBandEnabled,
                  fitMethod: curveFitMethod,
                  randomSeed,
                  sharedRandom: Boolean(randomSeed),
                })
              : await bootstrapCurveCIFromCounts(
                  modelDiscoveryCounts,
                  modelStartYear,
                  fitGompertz,
                  bootstrapSamples,
                  params => params.a,
                  {
                    randomFn,
                    curveFn: gompertzCurve,
                    includeCurve: curveBandEnabled,
                    fitMethod: curveFitMethod,
                    yieldEvery,
                    yieldFn: yieldToMain,
                  }
                )
            : null;
        }

        updateStage(t('estimationPage.progressFinalize'), 95);
        await yieldToMain();

        if (!isActive()) return;

        const sampleSize = modelDiscoveryCounts.length;
        const solowExpected = solowSmithBase
          ? computeExpectedDiscoveryCounts(solowSmithBase, modelStartYear, endYear)
          : null;
        const solowLogL = solowExpected
          ? computePoissonLogL(modelDiscoveryCounts, solowExpected)
          : null;
        const solowGof = solowSmithBase && bootstrapSamples > 0
          ? workerClient
            ? await workerClient.runJob('BOOTSTRAP_SOLOW_GOF', {
                fit: solowSmithBase,
                discoveryCounts: modelDiscoveryCounts,
                startYear: modelStartYear,
                endYear,
                iterations: Math.max(30, Math.min(200, bootstrapSamples)),
                randomSeed,
                sharedRandom: Boolean(randomSeed),
              })
            : await bootstrapSolowSmithGof(
                solowSmithBase,
                modelDiscoveryCounts,
                modelStartYear,
                endYear,
                Math.max(30, Math.min(200, bootstrapSamples)),
                {
                  randomFn,
                  yieldEvery,
                  yieldFn: yieldToMain,
                }
              )
          : null;

        const logisticExpected = logistic
          ? computeExpectedCurveCounts(logisticCurve, logistic.params, modelDiscoveryCounts, modelStartYear)
          : null;
        const logisticLogL = logisticExpected
          ? computePoissonLogL(modelDiscoveryCounts, logisticExpected)
          : null;

        const gompertzExpected = gompertz
          ? computeExpectedCurveCounts(gompertzCurve, gompertz.params, modelDiscoveryCounts, modelStartYear)
          : null;
        const gompertzLogL = gompertzExpected
          ? computePoissonLogL(modelDiscoveryCounts, gompertzExpected)
          : null;

        const logisticDiagnostics = logisticExpected
          ? {
              aic: computeAIC(logisticLogL, 3),
              aicc: computeAICc(logisticLogL, 3, sampleSize),
              holdoutRmse: computeHoldoutRMSE(modelDiscoveryCounts, logisticExpected),
            }
          : null;
        const gompertzDiagnostics = gompertzExpected
          ? {
              aic: computeAIC(gompertzLogL, 3),
              aicc: computeAICc(gompertzLogL, 3, sampleSize),
              holdoutRmse: computeHoldoutRMSE(modelDiscoveryCounts, gompertzExpected),
            }
          : null;

        let curveModelAverage = null;
        if (
          curveFitMethod === 'poisson'
          && logistic
          && gompertz
          && Number.isFinite(logisticDiagnostics?.aicc)
          && Number.isFinite(gompertzDiagnostics?.aicc)
        ) {
          const weights = computeAkaikeWeights([logisticDiagnostics.aicc, gompertzDiagnostics.aicc]);
          if (weights) {
            const [logisticWeight, gompertzWeight] = weights;
            curveModelAverage = {
              estimate: logisticWeight * logistic.params.L + gompertzWeight * gompertz.params.a,
              weights: {
                logistic: logisticWeight,
                gompertz: gompertzWeight,
              },
            };
          }
        }

        const results = {
          modelStartYear,
          curveFitMethod,
          curveModelAverage,
          logistic: logistic
            ? {
                ...logistic,
                ci: logisticCI,
                diagnostics: logisticDiagnostics,
              }
            : null,
          gompertz: gompertz
            ? {
                ...gompertz,
                ci: gompertzCI,
                diagnostics: gompertzDiagnostics,
              }
            : null,
          solowSmith: solowSmithBase
            ? {
                ...solowSmithBase,
                ci: solowSmithProfileCI,
                bootstrapCI: solowSmithBootstrapCI,
                diagnostics: solowExpected
                  ? {
                      aic: computeAIC(solowLogL, 2),
                      aicc: computeAICc(solowLogL, 2, sampleSize),
                      holdoutRmse: computeHoldoutRMSE(modelDiscoveryCounts, solowExpected),
                      gofPValue: solowGof?.pValue ?? null,
                    }
                  : null,
              }
            : null,
        };

        setResult(results);
        updateProgress(100);

        if (!results.logistic && !results.gompertz && !results.solowSmith) {
          setError(t('estimationPage.fitFailed'));
        }
      } catch (error) {
        if (isActive()) {
          setError(t('estimationPage.fitFailed'));
        }
      } finally {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      }
    });

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [
    task.cancel,
    task.reset,
    task.setError,
    task.run,
    modelFitPoints,
    modelSolowPoints,
    modelDiscoveryCounts,
    modelStartYear,
    endYear,
    bootstrapSamples,
    curveBandEnabled,
    curveFitMethod,
    randomSeed,
    getRandomFn,
    t,
    runTrigger,
  ]);

  return task;
};
